import { useCallback, useEffect, useState } from 'react';
import { Box, Container, Paper, Stack } from '@mui/material';
import { useSelector } from 'react-redux';
import Back from '../../../components/common/Back.component';
import Loading from '../../../components/common/Loading.component';
import Alert from '../../../components/common/Alert.component';
import AddView from '../../../views/Add.view';
import { fetchAPI } from '../../../api/request';
import _ from 'lodash';
import { API as handoverAPI } from '../../../module/handover/api';

const HandoverAddPage = ({ module }) => {
    const conf = module.create.conf;

    const { jwt } = useSelector((state) => state.login);
    const userAuth = useSelector((state) => state.login?.user);

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);
    const [currentUserNauticaId, setCurrentUserNauticaId] = useState(null);

    const canCreate =
        userAuth?.permissions?.[_.toUpper(module.tag)]?.CREATE === 'true';

    const onError = useCallback((err) => {
        setSuccess(false);
        setLoading(false);
        setError(err);
    }, []);

    useEffect(() => {
        const fetchCurrentUserNautica = async () => {
            try {
                if (!userAuth?.id) return;

                const resp = await fetchAPI(
                    {
                        path: `/users/${userAuth.id}?populate[id_users_nautica]=*`,
                        method: 'GET',
                        auth: true,
                        fallback: null,
                    },
                    {},
                    {},
                    {},
                    jwt
                );

                if (resp?.id_users_nautica?.id) {
                    setCurrentUserNauticaId(resp.id_users_nautica.id);
                }
            } catch (e) {}
        };

        if (jwt && userAuth?.id) fetchCurrentUserNautica();
    }, [jwt, userAuth]);

    const onSubmit = useCallback(
        async (values) => {
            try {
                if (!currentUserNauticaId) {
                    setError({
                        response: {
                            data: {
                                error: {
                                    message: 'No users-nautica associated with the logged user.',
                                },
                            },
                        },
                    });
                    return;
                }

                setLoading(true);
                setSuccess(false);
                setError(false);

                const payload = {
                    id_department: values?.id_department?.id || null,
                    id_team: values?.id_team?.id || null,
                    id_yacht: values?.id_yacht?.id || null,
                    assignee_user_id: values?.assignee_user_id?.id || null,
                    type: values?.type?.id || null,
                    priority: values?.priority?.id || 'MEDIUM',
                    status: values?.status?.id || 'OPEN',
                    title: values?.title,
                    description: values?.description,
                    created_by_user_id: currentUserNauticaId,
                    date: values?.date || null,
                };

                await fetchAPI(handoverAPI.createTicket, {}, {}, payload, jwt);

                setLoading(false);
                setSuccess(true);
            } catch (e) {
                onError(e);
            }
        },
        [jwt, onError, currentUserNauticaId]
    );

    return (
        <Container disableGutters maxWidth={false} sx={{ height: '100%' }}>
            <Stack
                spacing={2}
                sx={{ padding: 2 }}
                justifyContent='center'
                alignItems='center'
            >
                <Back title={module.tag} />
                <Container>
                    {loading && <Loading />}
                    {success && <Alert severity='success'>{'Success: Add'}</Alert>}
                    {error && (
                        <Alert severity='error'>
                            {error?.response?.data?.error?.message
                                ? error?.response?.data?.error?.message
                                : 'An error has occurred'}
                        </Alert>
                    )}

                    <Paper variant='outlined'>
                        <Box sx={{ padding: 3 }}>
                            {canCreate ? (
                                <AddView
                                    conf={conf}
                                    disabled={loading || success}
                                    onAddSubmit={onSubmit}
                                    onAddError={onError}
                                    onAddSuccess={() => setSuccess(true)}
                                />
                            ) : (
                                <Alert severity='error'>{'No permissions'}</Alert>
                            )}
                        </Box>
                    </Paper>
                </Container>
            </Stack>
        </Container>
    );
};

export default HandoverAddPage;